import React, { useState, useEffect, useCallback, useRef } from "react";
import { Link } from "react-router-dom";
import { PackageSearch, Calendar, MapPin, Egg, Bird, Ham, Drumstick, Filter, X } from "lucide-react";
import { productsAPI } from "../../api";
import Hint from "../Onboarding/Hint";
import "./Products.css";

const PRODUCT_TYPE_ICONS = {
  eggs: Egg,
  poultry: Bird,
  other: Ham,
  chicken: Drumstick,
};

// Fonction pour formater l'unité avec le pluriel correct
const formatUnit = (unit, quantity) => {
  if (quantity > 1) {
    if (unit === "poulet") return "poulets";
    if (unit === "pièce") return "pièces";
    if (unit === "lot") return "lots";
  }
  return unit;
};

const ProductsList = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [filters, setFilters] = useState({
    search: "",
    product_type: "",
    category: "",
    min_price: "",
    max_price: "",
    city: "",
  });
  const [categories, setCategories] = useState([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const filterRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (e) => {
      if (filterRef.current && !filterRef.current.contains(e.target)) {
        setIsFilterOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const loadCategories = useCallback(async () => {
    try {
      const response = await productsAPI.getCategories();
      setCategories(response.data);
    } catch (error) {
      console.error("Error loading categories:", error);
    }
  }, []);

  const loadProducts = useCallback(async () => {
    setLoading(true);
    setError("");
    try {
      const params = {
        ...filters,
        min_price: filters.min_price || undefined,
        max_price: filters.max_price || undefined,
        available_only: false, // Permettre de voir les produits futurs
      };
      Object.keys(params).forEach((key) => {
        if (params[key] === "" || params[key] === null) {
          delete params[key];
        }
      });
      
      const response = await productsAPI.getAll(params);
      setProducts(response.data);
    } catch (error) {
      setError("Erreur lors du chargement des produits");
      console.error(error);
    } finally {
      setLoading(false);
    }
  }, [filters]);

  useEffect(() => {
    loadCategories();
    loadProducts();
  }, [loadCategories, loadProducts]);

  useEffect(() => {
    const timeoutId = setTimeout(() => {
      loadProducts();
    }, 300);
    return () => clearTimeout(timeoutId);
  }, [filters, loadProducts]);

  const handleFilterChange = (e) => {
    setFilters({
      ...filters,
      [e.target.name]: e.target.value,
    });
  };

  const activeFilterCount = ["product_type", "category", "city", "min_price", "max_price"]
    .filter((key) => filters[key]).length;

  const resetFilters = () => {
    setFilters({
      ...filters,
      product_type: "",
      category: "",
      city: "",
      min_price: "",
      max_price: "",
    });
  };

  if (loading && products.length === 0) {
    return (
      <div className="container main-content">
        <div className="loading">
          <div className="spinner"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="container main-content">
      <div className="page-header">
        <h1>Produits disponibles</h1>
        <p>Trouvez les meilleurs produits d'élevage directement auprès des éleveurs</p>
      </div>

      <Hint id="products_browsing">
        Filtrez par type, ville ou prix pour affiner votre recherche. Cliquez sur un produit pour voir le détail et passer commande.
      </Hint>

      <div className="filters-section" data-tour="products-filters">
        <div className="filters-bar">
          <div className="form-group filters-search-group">
            <input
              type="text"
              name="search"
              className="form-control"
              placeholder="Rechercher un produit..."
              value={filters.search}
              onChange={handleFilterChange}
            />
          </div>

          <div className="filters-dropdown" ref={filterRef}>
            <button
              type="button"
              className="btn btn-outline filters-toggle-btn"
              onClick={() => setIsFilterOpen((open) => !open)}
              aria-expanded={isFilterOpen}
            >
              <Filter size={16} strokeWidth={2.25} /> Filtres
              {activeFilterCount > 0 && (
                <span className="filters-count-badge">{activeFilterCount}</span>
              )}
            </button>

            {isFilterOpen && (
              <div className="filters-panel">
                <div className="filters-panel-header">
                  <span>Filtres</span>
                  <button
                    type="button"
                    className="filters-panel-close"
                    onClick={() => setIsFilterOpen(false)}
                    aria-label="Fermer les filtres"
                  >
                    <X size={16} strokeWidth={2.25} />
                  </button>
                </div>

                <div className="form-group">
                  <label className="form-label">Type de produit</label>
                  <select
                    name="product_type"
                    className="form-control"
                    value={filters.product_type}
                    onChange={handleFilterChange}
                  >
                    <option value="">Tous les types</option>
                    <option value="chicken">🐔 Poulet</option>
                    <option value="eggs">🥚 Œufs</option>
                    <option value="poultry">🦆 Autre volaille</option>
                    <option value="other">🐷 Autres produits</option>
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Catégorie</label>
                  <select
                    name="category"
                    className="form-control"
                    value={filters.category}
                    onChange={handleFilterChange}
                  >
                    <option value="">Toutes les catégories</option>
                    {categories.map((cat) => (
                      <option key={cat} value={cat}>
                        {cat}
                      </option>
                    ))}
                  </select>
                </div>

                <div className="form-group">
                  <label className="form-label">Ville</label>
                  <input
                    type="text"
                    name="city"
                    className="form-control"
                    placeholder="Ville"
                    value={filters.city}
                    onChange={handleFilterChange}
                  />
                </div>

                <div className="filters-price-row">
                  <div className="form-group">
                    <label className="form-label">Prix min</label>
                    <input
                      type="number"
                      name="min_price"
                      className="form-control"
                      placeholder="F CFA"
                      value={filters.min_price}
                      onChange={handleFilterChange}
                    />
                  </div>

                  <div className="form-group">
                    <label className="form-label">Prix max</label>
                    <input
                      type="number"
                      name="max_price"
                      className="form-control"
                      placeholder="F CFA"
                      value={filters.max_price}
                      onChange={handleFilterChange}
                    />
                  </div>
                </div>

                {activeFilterCount > 0 && (
                  <button type="button" className="filters-reset-btn" onClick={resetFilters}>
                    <X size={14} strokeWidth={2.25} /> Réinitialiser les filtres
                  </button>
                )}
              </div>
            )}
          </div>
        </div>
      </div>

      {error && <div className="alert alert-error">{error}</div>}

      {products.length === 0 ? (
        <div className="empty-state">
          <div className="empty-state-icon"><PackageSearch size={56} strokeWidth={1.75} /></div>
          <h3>Aucun produit trouvé</h3>
          <p>Essayez de modifier vos critères de recherche</p>
        </div>
      ) : (
        <div className="grid grid-3">
          {products.map((product) => (
            <Link
              key={product.id}
              to={`/products/${product.id}`}
              className="product-card card"
            >
              <div className="product-image">
                {(() => {
                  const firstImage = product.images && product.images.length > 0 
                    ? product.images[0].image_url 
                    : product.image_url;
                  
                  return firstImage ? (
                    <img 
                      src={firstImage} 
                      alt={product.name}
                      onError={(e) => {
                        e.target.style.display = 'none';
                        e.target.nextSibling.style.display = 'flex';
                      }}
                    />
                  ) : null;
                })()}
                <div
                  className="product-image-placeholder"
                  style={{
                    display: (product.images && product.images.length > 0) || product.image_url ? 'none' : 'flex'
                  }}
                >
                  {(() => {
                    const TypeIcon = PRODUCT_TYPE_ICONS[product.product_type] || Drumstick;
                    return <TypeIcon size={56} strokeWidth={2} color="#fff" />;
                  })()}
                </div>
              </div>
              <div className="product-info">
                <h3 className="product-name">{product.name}</h3>
                <div style={{ display: "flex", gap: "0.5rem", flexWrap: "wrap", marginBottom: "0.5rem" }}>
                  {product.product_type && (
                    <span className="badge badge-info" style={{ fontSize: "0.8rem" }}>
                      {product.product_type === "chicken" ? "🐔 Poulet" : 
                       product.product_type === "eggs" ? "🥚 Œufs" : 
                       product.product_type === "poultry" ? "🦆 Volaille" : 
                       "🐷 Autre"}
                    </span>
                  )}
                  {product.is_live && (
                    <span className="badge badge-success" style={{ fontSize: "0.8rem" }}>
                      🐔 Vivant
                    </span>
                  )}
                  {product.category && (
                    <span className="badge badge-primary">{product.category}</span>
                  )}
                  {product.available_from && new Date(product.available_from) > new Date() && (
                    <span className="badge badge-warning icon-inline" style={{ fontSize: "0.8rem" }}>
                      <Calendar size={13} strokeWidth={2.25} /> Disponible le {new Date(product.available_from).toLocaleDateString("fr-FR")}
                    </span>
                  )}
                </div>
                <p className="product-description">
                  {product.description || "Aucune description"}
                </p>
                <div className="product-details" style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", gap: "1rem", flexWrap: "wrap" }}>
                  <div className="product-price" style={{ display: "flex", alignItems: "baseline", gap: "0.5rem", flexWrap: "wrap" }}>
                    <span style={{ fontSize: "1.5rem", fontWeight: "bold" }}>
                      {product.unit === "lot" && product.lot_price
                        ? product.lot_price.toLocaleString("fr-FR")
                        : product.price.toLocaleString("fr-FR")}
                    </span>
                    <span style={{ fontSize: "1.5rem", fontWeight: "bold" }}>
                      F CFA {product.unit === "lot" && product.lot_size ? `/ lot (${product.lot_size} ${product.lot_size > 1 ? "éléments" : "élément"})` : `/ ${product.unit}`}
                    </span>
                  </div>
                  <div className="product-stock" style={{ fontSize: "0.95rem", color: "var(--text-primary)", whiteSpace: "nowrap" }}>
                    <strong>Stock :</strong> {product.stock} {formatUnit(product.unit, product.stock)}
                  </div>
                </div>
                <div className="product-owner">
                  <span>Par {product.owner?.name || "Éleveur"}</span>
                  {product.owner?.city && (
                    <span className="product-location icon-inline"><MapPin size={13} strokeWidth={2.25} /> {product.owner.city}</span>
                  )}
                </div>
              </div>
            </Link>
          ))}
        </div>
      )}
    </div>
  );
};

export default ProductsList;
